import { httpClient } from './api/httpClient';
import { isMockEnabled, mockResponse } from './api/mockAdapter';
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import {
  MOCK_BRIEFING,
  MOCK_BRIEFING_SCHEDULE,
  MOCK_BRIEFING_BATCHES,
} from 'mock/pipeline.mock';

export const toBriefing = (raw) =>
  raw && {
    id: raw.id,
    articleReferenceId: raw.article_reference_id ?? '',
    rootArticleId: raw.root_article_id ?? '',
    currentStage: raw.current_stage ?? 'briefing',
    workflowState: raw.workflow_state ?? 'active',
    documentVersion: raw.document_version ?? 1,
    status: raw.status ?? 'routed',
    postBriefingDecision: raw.post_briefing_decision ?? null,
    isDispatched: raw.workflow_state === 'completed',
    whatsappSent: raw.whatsapp_sent ?? false,
    whatsappHistory: raw.whatsapp_history ?? [],
    newspaperName: raw.newspaper_name ?? '',
    edition: raw.edition ?? '',
    keywords: raw.keywords ?? [],
    articleTitle: raw.article_title ?? null,
    headline: raw.headline ?? null,
    pageNumber: raw.page_number ?? null,
    language: raw.language ?? null,
    articleImageUrl: raw.article_image_url ?? null,
    articleLink: raw.article_image_url ?? null,
    newspaperPageImageUrl: raw.newspaper_page_image_url ?? null,
    newspaperLink: raw.newspaper_page_image_url ?? null,
    mergedPdfUrl: raw.merged_pdf_url ?? null,
    mergedPdfStatus: raw.merged_pdf_status ?? null,
    source: raw.source?.type ?? raw.source ?? null,
    isManualUpload: raw.is_manual_upload ?? false,
    previousStage: raw.previous_stage ?? null,
    workflowTimeline: raw.workflow_timeline ?? [],
    notes: raw.notes ?? null,
    createdAt: raw.created_at ?? null,
    createdBy: raw.created_by ?? null,
    updatedAt: raw.updated_at ?? null,
  };

export const toBatch = (raw) =>
  raw && {
    id: raw.id ?? raw._id,
    batchNumber: raw.batch_number ?? raw.batchNumber,
    windowLabel: raw.time_slot ?? raw.window_label ?? raw.windowLabel ?? '',
    articleCount: raw.total_articles ?? raw.article_count ?? raw.articleCount ?? 0,
    sentAt: raw.sent_at ?? raw.sentAt ?? null,
    sentTo: raw.whatsapp_numbers ?? raw.sent_to ?? raw.sentTo ?? [],
    articleIds: raw.articles ? raw.articles.map((a) => a.briefing_id || a) : (raw.article_ids ?? raw.articleIds ?? []),
    articles: raw.articles ? raw.articles.map((a) => ({
      briefingId: a.briefing_id,
      articleReferenceId: a.article_reference_id,
      headline: a.headline,
      pageNumber: a.page_number,
      newspaperName: a.newspaper_name,
      edition: a.edition,
      keywords: a.keywords || [],
      articleDate: a.article_date,
    })) : [],
  };

export const toSchedule = (raw) =>
  raw && {
    id: raw.id,
    windows: (raw.windows || []).map((w) => ({ label: w.label, dispatchTime: w.dispatch_time ?? w.dispatchTime })),
    dispatchTo: raw.dispatch_to ?? raw.dispatchTo ?? [],
    status: raw.status,
  };

let store = MOCK_BRIEFING.map((r) => ({ ...r }));
let schedule = { ...MOCK_BRIEFING_SCHEDULE };
const batches = MOCK_BRIEFING_BATCHES.map((r) => ({ ...r }));

export const briefingService = {
  async list(params = {}) {
    if (isMockEnabled()) {
      let filtered = store;
      if (params.newspaper_name) {
        filtered = filtered.filter(a => a.newspaper_name === params.newspaper_name || a.newspaperName === params.newspaper_name);
      }
      return (await mockResponse(filtered)).map(toBriefing);
    }
    const data = await httpClient.get(API_ENDPOINTS.BRIEFING.LIST, { params });
    return (Array.isArray(data) ? data : data.items ?? []).map(toBriefing);
  },

  async get(id) {
    if (isMockEnabled()) return toBriefing(await mockResponse(store.find((r) => r.id === id)));
    return toBriefing(await httpClient.get(API_ENDPOINTS.BRIEFING.DETAIL(id)));
  },

  async listBatches() {
    if (isMockEnabled()) return (await mockResponse(batches)).map(toBatch);
    const data = await httpClient.get(API_ENDPOINTS.BRIEFING.WHATSAPP_HISTORY);
    return (Array.isArray(data) ? data : data.items ?? []).map(toBatch);
  },

  async getBatch(batchId) {
    if (isMockEnabled()) return toBatch(await mockResponse(batches.find((b) => b.id === batchId)));
    return toBatch(await httpClient.get(API_ENDPOINTS.BRIEFING.WHATSAPP_BATCH(batchId)));
  },

  async getSchedule() {
    if (isMockEnabled()) return toSchedule(await mockResponse(schedule));
    return toSchedule(await httpClient.get(API_ENDPOINTS.BRIEFING.SCHEDULE.GET));
  },

  async saveSchedule(model) {
    const payload = {
      windows: model.windows.map((w) => ({ label: w.label, dispatch_time: w.dispatchTime })),
      dispatch_to: model.dispatchTo,
      status: model.status ?? 'active',
    };
    if (isMockEnabled()) {
      schedule = { ...schedule, ...payload };
      return toSchedule(await mockResponse(schedule));
    }
    return toSchedule(await httpClient.put(API_ENDPOINTS.BRIEFING.SCHEDULE.PUT, payload));
  },

  async setDecision(id, decision, documentVersion) {
    if (isMockEnabled()) {
      store = store.map((r) => (r.id === id ? { ...r, post_briefing_decision: decision } : r));
      return toBriefing(await mockResponse(store.find((r) => r.id === id)));
    }
    return toBriefing(
      await httpClient.post(API_ENDPOINTS.BRIEFING.DECISION(id), {
        decision,
        document_version: documentVersion,
      })
    );
  },

  async dispatch(id, documentVersion) {
    if (isMockEnabled()) {
      store = store.map((r) => (r.id === id ? { ...r, workflow_state: 'completed' } : r));
      return toBriefing(await mockResponse(store.find((r) => r.id === id)));
    }
    return toBriefing(
      await httpClient.post(API_ENDPOINTS.BRIEFING.DISPATCH(id), { document_version: documentVersion })
    );
  },
};

export default briefingService;
